/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './modal-card.stache';
import cloudinaryUtil from 'scripts/utils/cloudinary';
import DomEventsManager from 'scripts/dom-events-manager';
import Swiper from 'swiper';
import _ from 'lodash';
import can from 'can';
import 'dotdotdot';
import dtm from 'scripts/services/dtm';
import domUtil from 'scripts/utils/dom';

Component.extend({
    tag: 'modal-card',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            showCover: {
                type: 'boolean',
                value: true,
            },
            cardLocation: {
                type: 'string',
                value: null,
            },
            imageSrc: {
                get() {
                    const image = this.attr('coverImage');
                    if (image.poster && image.poster.path) {
                        return image.poster.path;
                    }
                    return cloudinaryUtil.getCloudinaryPath(
                        image.identifier,
                        image.width,
                        image.height,
                        image.cropMode,
                    );
                },
            },
            photoGallery: {
                Type: can.List,
                Value: can.List,
            },
            photoGalleryActiveSlide: {
                type: 'number',
                value: 0,
            },
        },
        isPhotoActive(photoIndex) {
            let activeIndex = this.attr('photoGalleryActiveSlide');
            return (
                (activeIndex >= photoIndex - 2 &&
                    activeIndex <= photoIndex + 2) ||
                photoIndex <= 1 ||
                photoIndex >= this.attr('photoGallery').length - 1
            );
        },
    },
    events: {
        inserted() {
            DomEventsManager.subscribe(this);

            if (this.viewModel.attr('description')) {
                this.modalDescription = this.element
                    .find('modal-description')
                    .viewModel();
            }
            this.socialShare = this.element.find('social-share').viewModel();

            switch (this.viewModel.attr('cardType')) {
                case 'video':
                    this.initVideoCard();
                    break;
                case 'photoGallery':
                    this.initPhotoGallery();
                    break;
            }

            can.$(this.element)
                .find('.s-photo-caption')
                .dotdotdot();
            can.$(this.element)
                .find('.s-card__text')
                .dotdotdot();
            setTimeout(() => {
                this.element.find('.s-gallery-hidden-start-focus').focus();
            }, 1000);
        },

        onWindowResize() {
            this.debouncedWindowResize();
        },

        debouncedWindowResize: _.debounce(function() {
            if (this.photoGallerySwiper) {
                can.$(
                    this.photoGallerySwiper.slides[
                        this.photoGallerySwiper.activeIndex
                    ],
                )
                    .find('.s-photo-caption')
                    .dotdotdot();
            }
        }, 500),

        removed() {
            DomEventsManager.unsubscribe(this);

            if (
                this.viewModel.attr('description') &&
                this.modalDescription.$popup
            ) {
                this.modalDescription.$popup.remove();
            }
            if (this.socialShare.$popup) {
                this.socialShare.$popup.remove();
            }
            if (this.photoGallerySwiper) {
                this.photoGallerySwiper.destroy(false, false);
                this.photoGallerySwiper = null;
            }
        },

        initPhotoGallery() {
            let swiperContainer = this.element.find(
                '.swiper-container.s-photo-gallery',
            );

            _.forEach(this.viewModel.photos, (photo) => {
                photo.src = cloudinaryUtil.getCloudinaryPath(
                    photo.identifier,
                    photo.width,
                    photo.height,
                    photo.cropMode,
                );
            });
            this.viewModel.attr('photoGallery', this.viewModel.photos);

            this.photoGallerySwiper = new Swiper(swiperContainer, {
                direction: 'horizontal',
                slidesPerView: 1,
                loop: this.viewModel.attr('isMobile'),
                nested: true,
                preloadImages: false,
                watchSlidesVisibility: false,
                centeredSlides: true,
                lazy: {
                    loadPrevNext: true,
                },
                pagination: {
                    el: this.element.find('.s-photo-gallery-pagination'),
                    type: 'fraction',
                },
                renderFraction(currentClassName, totalClassName) {
                    return (
                        '<span class="' +
                        currentClassName +
                        '"></span>' +
                        ' of ' +
                        '<span class="' +
                        totalClassName +
                        '"></span>'
                    );
                },
                onLazyImageReady: (swiper, slide, image) => {
                    swiper.updateContainerSize();
                    swiper.updateSlidesSize();
                },
            });

            this.photoGallerySwiper.on('slideChangeTransitionStart', () => {
                this.viewModel.attr(
                    'photoGalleryActiveSlide',
                    this.photoGallerySwiper.activeIndex,
                );
                can.$(
                    this.photoGallerySwiper.slides[
                        this.photoGallerySwiper.activeIndex
                    ],
                )
                    .find('.s-photo-caption')
                    .dotdotdot();
            });
        },

        initVideoCard() {
            let self = this;
            const $image = this.element.find('.s-card__image');
            const coverImage = this.viewModel.attr('coverImage');
            this.player = this.element.find('brightcove-video').viewModel();
            this.player
                .on('brightcoveready', (type, payload) => {
                    coverImage.poster = coverImage.poster || {};
                    coverImage.poster.width = coverImage.poster.width
                        ? coverImage.poster.width
                        : domUtil.getElementCssProperty($image, 'max-width');
                    coverImage.poster.height = coverImage.poster.height
                        ? coverImage.poster.height
                        : domUtil.getElementCssProperty($image, 'max-height');
                    coverImage.poster.path =
                        coverImage.poster.path ||
                        cloudinaryUtil.getCloudinaryPath(
                            coverImage.identifier,
                            coverImage.width || coverImage.poster.width,
                            coverImage.height || coverImage.poster.height,
                            coverImage.cropMode,
                        );
                })
                .on('videoplay', () => {
                    self.viewModel.attr('showCover', false);
                });
        },

        '.s-card__image--poster click'() {
            this.player.play();

            const videoName = this.viewModel.attr('videoName');
            const cardLocation = this.viewModel.attr('cardLocation');
            if (cardLocation === 'timeline') {
                dtm.trackHistoryVideoLauch(
                    videoName,
                    'History Timeline Content',
                );
            } else if (cardLocation === 'collections') {
                dtm.trackHistoryVideoLauch(
                    videoName,
                    'History Collection Content',
                );
            }
        },

        '.js-mobile-card-button click'(element, event) {
            this.element.find('.s-card').toggleClass('show-text');
        },

        '.photo-gallery--next click'(element, event) {
            if (this.photoGallerySwiper.isEnd) {
                this.photoGallerySwiper.slideTo(0, 300);
            } else {
                this.photoGallerySwiper.slideNext();
            }
        },

        '.photo-gallery--prev click'(element, event) {
            if (this.photoGallerySwiper.isBeginning) {
                this.photoGallerySwiper.slideTo(
                    this.photoGallerySwiper.slides.length - 1,
                    300,
                );
            } else {
                this.photoGallerySwiper.slidePrev();
            }
        },

        '.js-trap-focus focus'() {
            const swiperContainer = this.element.find('.s-card__container');
            const focusSelector =
                'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), div[role="button"]';
            const focusEls = swiperContainer.find(focusSelector);
            if (focusEls && focusEls[0]) {
                focusEls[0].focus();
            }
        },

        '.s-gallery-hidden-start-focus blur'($el) {
            $el.attr('tabindex', '-1');
        },
    },

    helpers: {
        collectionClass(options) {
            const collectionTags = options.context['collectionTags'];
            return collectionTags && collectionTags.length
                ? collectionTags[0].toLowerCase().replace(' ', '-')
                : null;
        },
        collectionIcon(options) {
            const collectionTags = options.context['collectionTags'];
            if (collectionTags && collectionTags.length) {
                switch (collectionTags[0]) {
                    case 'Iconic Venues':
                        return 'sprites.svg#icon-tee';
                    case 'Legendary Champions':
                        return 'sprites.svg#icon-trophy';
                    case 'Signature Moments':
                        return 'sprites.svg#icon-star';
                }
            }
            return null;
        },
        collectionName(options) {
            const collectionTags = options.context['collectionTags'];
            return collectionTags && collectionTags.length
                ? collectionTags[0]
                : null;
        },
    },
});
